import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';

// PrimeNG
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { ProjectOption } from '../../models/project.models';
import {
  TaskGroupOption,
  TaskStatusResponse,
  TaskTypeOption,
  TaskTypeResponse,
} from '../../models/task-type.models';
import {
  CreateTaskRequest,
  LinkType,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  TaskCreateContext,
  TaskEntityLink,
  TaskEntityType,
  TaskPriority,
} from '../../models/task.models';
import { UserOption } from '../../models/user.models';
import { TaskProjectService } from '../../services/task-project.service';
import { TaskTypeService } from '../../services/task-type.service';
import { TaskService } from '../../services/task.service';
import { UserService } from '../../services/user.service';
import { TooltipModule } from 'primeng/tooltip';
import { AppService } from 'src/app/app.service';
import { MessageService } from 'primeng/api';

@Component({
  standalone: true,
  selector: 'app-task-quick-create-modal',
  templateUrl: './task-quick-create-modal.component.html',
  styleUrls: ['./task-quick-create-modal.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    DropdownModule,
    CalendarModule,
    InputTextModule,
    InputTextareaModule,
    ButtonModule,
    ProgressSpinnerModule,
    MultiSelectModule,
    TooltipModule
  ],
})
export class TaskQuickCreateModalComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();

  // Inputs/Outputs for simple binding
  @Input() visible: boolean = false;
  @Input() context: TaskCreateContext | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() taskCreated = new EventEmitter<number | null>();

  // Modal state
  isLoading = false;
  isSaving = false;
  // showFullForm = false;

  // Form
  taskForm: FormGroup;

  // Dropdowns data
  taskTypeGroups: TaskGroupOption[] = [];
  rawTaskTypes: TaskTypeResponse[] = [];
  allTaskTypes: TaskTypeOption[] = [];
  taskTypes: TaskTypeOption[] = []; // Filtered based on selected group
  taskStatuses: TaskStatusResponse[] = [];
  projects: ProjectOption[] = [];
  users: UserOption[] = [];
  priorities = [
    TaskPriority.CRITICAL,
    TaskPriority.HIGH,
    TaskPriority.NORMAL,
    TaskPriority.LOW,
  ].map((value) => ({
    value,
    label: PRIORITY_LABELS[value],
    color: PRIORITY_COLORS[value],
  }));

  entityTypeOptions: { value: TaskEntityType; label: string; icon?: string; color?: string }[] = [];

  // Autocomplete suggestions keyed by FormArray row index
  entitySuggestions: { [index: number]: { id: string; display_name: string }[] } = {};
  // Display values for the autocomplete inputs (may be string or suggestion object)
  entityDisplayValues: { [index: number]: any } = {};

  linkTypeOptions = [
    { value: LinkType.PRIMARY, label: 'Primary' },
    { value: LinkType.RELATED, label: 'Related' },
    { value: LinkType.REFERENCE, label: 'Reference' },
  ];

  // Selected task type details
  selectedTaskType: TaskTypeResponse | null = null;

  subscriptionCount = 0;

  constructor(
    private fb: FormBuilder,
    private taskService: TaskService,
    private taskTypeService: TaskTypeService,
    private projectService: TaskProjectService,
    private userService: UserService,
    private appService: AppService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.taskForm = this.createForm();
  }

  ngOnInit(): void {
    // Load initial data
    this.loadTaskTypeGroups();
    this.loadTaskTypes();
    this.loadProjects();
    this.loadUsers();
    this.loadEntityTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue) {
      this.onModalOpen();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      task_type_group_id: [null],
      task_type_id: [null, Validators.required],
      status_id: [null, Validators.required],
      priority: [TaskPriority.NORMAL, Validators.required],
      subject: ['', [Validators.required, Validators.maxLength(500)]],
      description: [''],
      assigned_to: [null],
      due_at: [null],
      project_ids: [[]],
      entities: this.fb.array([]),
    });
  }

  get entitiesArray(): FormArray {
    return this.taskForm.get('entities') as FormArray;
  }

  private createEntityLinkGroup(
    entityType = '',
    entityId = '',
    linkType = LinkType.PRIMARY
  ): FormGroup {
    return this.fb.group({
      entity_type: [entityType],
      entity_id: [entityId],
      link_type: [linkType],
    });
  }

  addEntityLink(): void {
    this.entitiesArray.push(this.createEntityLinkGroup());
  }

  removeEntityLink(index: number): void {
    this.entitiesArray.removeAt(index);
  }

  private getCurrentUserId(): number | null {
    const currentUser = JSON.parse(localStorage.getItem('optplanner_user') || '{}');
    const id = currentUser?.id;
    if (id == null) {
      return null;
    }
    const numericId = Number(id);
    return Number.isNaN(numericId) ? null : numericId;
  }

  private applyDefaultAssignee(): void {
    const userId = this.getCurrentUserId();
    if (userId == null || !this.users.some((user) => user.value === userId)) {
      return;
    }
    this.taskForm.patchValue({ assigned_to: userId });
  }

  /** Resolve option for selectedItem template when PrimeNG has not matched yet. */
  getPriorityDisplayOption(option: unknown): { value: number; label: string; color: string } | null {
    if (option && typeof option === 'object' && 'label' in option && 'value' in option) {
      return option as { value: number; label: string; color: string };
    }
    const raw = this.taskForm.get('priority')?.value;
    const value =
      typeof option === 'number'
        ? option
        : typeof raw === 'number'
          ? raw
          : raw?.value;
    if (value == null) {
      return null;
    }
    return this.priorities.find((p) => p.value === value) ?? null;
  }

  private applyDefaultPriority(): void {
    this.taskForm.patchValue({ priority: TaskPriority.NORMAL });
  }

  /** Re-apply after the form mounts (*ngIf) so PrimeNG can resolve the selected label. */
  private scheduleDefaultFormControls(): void {
    setTimeout(() => {
      this.applyDefaultPriority();
      this.applyDefaultAssignee();
      this.cdr.detectChanges();
    });
  }

  private async ensureModalDataLoaded(): Promise<void> {
    const loads: Promise<void>[] = [];
    if (this.taskTypeGroups.length === 0) {
      loads.push(this.loadTaskTypeGroups());
    }
    if (this.rawTaskTypes.length === 0) {
      loads.push(this.loadTaskTypes());
    }
    if (this.projects.length === 0) {
      loads.push(this.loadProjects());
    }
    if (this.users.length === 0) {
      loads.push(this.loadUsers());
    }
    if (this.entityTypeOptions.length === 0) {
      loads.push(this.loadEntityTypes());
    }
    await Promise.all(loads);
  }

  private async onModalOpen(): Promise<void> {
    this.isLoading = true;

    try {
      await this.ensureModalDataLoaded();

      this.taskForm.reset({
        priority: TaskPriority.NORMAL,
        project_ids: [],
        assigned_to: this.getCurrentUserId(),
      });
      // Clear entities FormArray — reset() does not clear FormArrays
      while (this.entitiesArray.length) {
        this.entitiesArray.removeAt(0);
      }

      if (this.context) {
        this.applyContext(this.context);
      }
    } catch (error) {
      console.error('Error loading modal data:', error);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
      this.scheduleDefaultFormControls();
    }
  }

  private async loadTaskTypeGroups(): Promise<void> {
    try {
      const groups = await this.taskTypeService.getTaskTypeGroups(true).toPromise();
      this.taskTypeGroups = (groups || []).map((g) => ({
        value: g.id,
        label: g.name,
        code: g.code,
        // color: g.color,
        icon: g.icon,
      }));
    } catch (error) {
      console.error('Error loading task type groups:', error);
    }
  }

  private async loadTaskTypes(): Promise<void> {
    try {
      const types = await this.taskTypeService.getTaskTypes(undefined, true).toPromise();
      this.rawTaskTypes = types || [];
      this.allTaskTypes = this.taskTypeService.mapTaskTypesToOptions(this.rawTaskTypes);
      // Initially show all types
      this.taskTypes = [...this.allTaskTypes];
    } catch (error) {
      console.error('Error loading task types:', error);
    }
  }

  async onTaskTypeGroupChange(): Promise<void> {
    const groupId = this.taskForm.get('task_type_group_id')?.value;

    if (!groupId) {
      // No group selected, show all task types
      this.taskTypes = [...this.allTaskTypes];
      this.taskStatuses = [];
      this.taskForm.patchValue({ status_id: null });
    } else {
      // Filter task types by selected group
      // We need to get the full task type data to filter by group_id
      this.filterTaskTypesByGroup(groupId);

      // Load statuses for the selected group
      try {
        const statuses = await this.taskTypeService.getTaskStatuses(groupId, true).toPromise();
        this.taskStatuses = statuses || [];

        // Set default status (first non-closed status)
        const defaultStatus = this.taskStatuses.find((s) => !s.is_closed);
        if (defaultStatus) {
          this.taskForm.patchValue({ status_id: defaultStatus.id });
        }
      } catch (error) {
        console.error('Error loading task statuses for group:', error);
      }
    }

    // Clear task type selection when group changes
    this.taskForm.patchValue({ task_type_id: null });
  }

  private async filterTaskTypesByGroup(groupId: number): Promise<void> {
    try {
      const types = await this.taskTypeService.getTaskTypes(groupId, true).toPromise();
      this.taskTypes = this.taskTypeService.mapTaskTypesToOptions(types || []);
    } catch (error) {
      console.error('Error filtering task types by group:', error);
    }
  }

  private async loadProjects(): Promise<void> {
    try {
      const projects = await this.projectService.getProjects(undefined, true).toPromise();
      this.projects = (projects || []).map((p) => ({
        value: p.id,
        label: p.name,
        isActive: p.is_active,
      }));
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }

  private async loadUsers(): Promise<void> {
    try {
      const users = await this.userService.getUsers(true).toPromise();
      this.users = this.userService.mapUsersToOptions(users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }

  private async loadEntityTypes(): Promise<void> {
    try {
      const types = await this.taskService.getEntityTypes().toPromise();
      this.entityTypeOptions = (types || []).map((t) => ({
        value: t.entity_type as TaskEntityType,
        label: t.display_name || t.entity_type,
        icon: t.icon || undefined,
        color: t.color || undefined,
      }));
    } catch (error) {
      console.error('Error loading entity types:', error);
    }
  }

  searchEntitySuggestions(query: string, rowIndex: number): void {
    const entityType = this.entitiesArray.at(rowIndex)?.get('entity_type')?.value;
    if (!entityType || !query || query.length < 2) {
      this.entitySuggestions[rowIndex] = [];
      return;
    }
    this.taskService.searchEntityType(entityType, query).subscribe({
      next: (res) => {
        this.entitySuggestions[rowIndex] = res.items || [];
      },
      error: () => {
        this.entitySuggestions[rowIndex] = [];
      },
    });
  }

  onEntityIdChange(value: any, rowIndex: number): void {
    if (value && typeof value === 'object') {
      const item = value.value ?? value;
      if (item?.display_name != null) {
        this.entityDisplayValues[rowIndex] = item.display_name;
        this.entitiesArray.at(rowIndex)?.patchValue({ entity_id: item.id });
        return;
      }
    }

    this.entityDisplayValues[rowIndex] = value;
    if (typeof value === 'string') {
      this.entitiesArray.at(rowIndex)?.patchValue({ entity_id: value });
    }
  }

  onEntitySelected(event: { value?: { id: string; display_name: string } }, rowIndex: number): void {
    const item = event?.value;
    if (!item?.id) {
      return;
    }
    this.entitiesArray.at(rowIndex)?.patchValue({ entity_id: item.id });
    this.entityDisplayValues[rowIndex] = item.display_name ?? item.id;
  }

  private buildEntityLinksPayload(): TaskEntityLink[] {
    const entityLinks: TaskEntityLink[] = [];

    if (this.context?.entityType && this.context?.entityId) {
      entityLinks.push({
        entity_type: this.context.entityType,
        entity_id: String(this.context.entityId).trim(),
        link_type: this.context.linkType || LinkType.PRIMARY,
        metadata: this.context.metadata,
      });
    }

    this.entitiesArray.controls.forEach((ctrl) => {
      const v = ctrl.value;
      if (v.entity_type && v.entity_id) {
        const entityId = String(v.entity_id).trim();
        const isDuplicate = entityLinks.some(
          (e) => e.entity_type === v.entity_type && e.entity_id === entityId
        );
        if (!isDuplicate) {
          entityLinks.push({
            entity_type: v.entity_type,
            entity_id: entityId,
            link_type: v.link_type || LinkType.PRIMARY,
          });
        }
      }
    });

    return entityLinks;
  }

  private async validateEntityLinksBeforeSave(): Promise<boolean> {
    const entities = this.buildEntityLinksPayload();

    const results = await Promise.all(
      entities.map(async (entity) => {
        try {
          await this.taskService
            .resolveEntityLink(entity.entity_type, entity.entity_id)
            .toPromise();
          return { valid: true, entity };
        } catch {
          return { valid: false, entity };
        }
      })
    );

    const invalid = results.find((r) => !r.valid);
    if (invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid ID',
        detail: `The ${invalid.entity.entity_type} ID "${invalid.entity.entity_id}" is invalid or does not exist.`,
      });
      return false;
    }

    return true;
  }

  private applyContext(context: TaskCreateContext): void {
    // Pre-select task type if suggested
    if (context.suggestedTaskTypeId) {
      this.taskForm.patchValue({ task_type_id: context.suggestedTaskTypeId });
      this.onTaskTypeChange();
    }

    // Pre-select priority if suggested
    if (context.suggestedPriority) {
      this.taskForm.patchValue({ priority: context.suggestedPriority });
    }
  }

  async onTaskTypeChange(): Promise<void> {
    const typeId = this.taskForm.get('task_type_id')?.value;
    if (!typeId) {
      this.selectedTaskType = null;
      this.taskStatuses = [];
      return;
    }

    try {
      // Get task type details from local cache
      this.selectedTaskType = this.rawTaskTypes.find((t) => t.id === typeId) || null;

      if (this.selectedTaskType) {
        // Load statuses for this type's group
        const statuses = await this.taskTypeService
          .getTaskStatuses(this.selectedTaskType.group_id, true)
          .toPromise();
        this.taskStatuses = statuses || [];

        // Set default status (first non-closed status)
        const defaultStatus = this.taskStatuses.find((s) => !s.is_closed);
        if (defaultStatus) {
          this.taskForm.patchValue({ status_id: defaultStatus.id });
        }

        // Default priority: task type default or Medium (NORMAL)
        this.taskForm.patchValue({
          priority: this.selectedTaskType.default_priority ?? TaskPriority.NORMAL,
        });

        // Calculate default due date
        if (this.selectedTaskType.default_due_days) {
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + this.selectedTaskType.default_due_days);
          this.taskForm.patchValue({ due_at: dueDate.toISOString().split('T')[0] });
        }
      }
    } catch (error) {
      console.error('Error loading task type details:', error);
    }
  }

  // toggleFullForm(): void {
  //   this.showFullForm = !this.showFullForm;
  // }

  async onSubmit(): Promise<void> {
    if (this.taskForm.invalid) {
      Object.keys(this.taskForm.controls).forEach((key) => {
        this.taskForm.get(key)?.markAsTouched();
      });
      return;
    }

    const isValid = await this.validateEntityLinksBeforeSave();
    if (!isValid) {
      return;
    }

    this.isSaving = true;
    this.appService.showCustomLoader(true);

    try {
      const formValue = this.taskForm.value;
      const assignedTo =
        formValue.assigned_to?.value ?? formValue.assigned_to ?? this.getCurrentUserId();

      // Build request
      const request: CreateTaskRequest = {
        task_type_id: formValue.task_type_id,
        status_id: formValue.status_id,
        priority: formValue.priority?.value ?? formValue.priority,
        subject: formValue.subject,
        description: formValue.description,
        assigned_to: assignedTo ?? undefined,
        due_at: formValue.due_at ? new Date(formValue.due_at).toISOString() : undefined,
        entities: this.buildEntityLinksPayload(),
        project_ids: formValue.project_ids || [],
      };

      // Create task
      const response = await this.taskService.createTask(request).toPromise();

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Task created successfully',
      });

      // Emit success and close
      this.taskCreated.emit(response.task_id);
      this.visible = false;
      this.visibleChange.emit(false);
      this.taskForm.reset();
    } catch (error) {
      console.error('Error creating task:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create task',
      });
    } finally {
      this.appService.showCustomLoader(false);
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.taskCreated.emit(null);
    this.taskForm.reset();
  }

  // Validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.taskForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.taskForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return 'This field is required';
      if (field.errors['maxlength'])
        return `Maximum ${field.errors['maxlength'].requiredLength} characters`;
    }
    return '';
  }
}